import { useState } from 'react';
import { Dialog, DialogTitle, DialogContent, DialogContentText, Button, Stack, Typography } from '@mui/material';
import { useTranslations } from '@/i18n/use-translations';
import type { TranslationKey } from '@/i18n/translation-key';
import OtpCode from '@/components/invitation-flow/otp-code';
import { usePairDeviceMutation } from '@/state/mutations/player/use-pair-device';
import { useNotifications } from '@/hooks/use-notifications';
import type { PlayerDto } from '@/types/dtos/player/response/player.dto';
import type { PlayerParentEntity } from '@/types/dtos/player/player-parent-entity';

interface RepairPlayerModalProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
  player: PlayerDto;
  entityId: string;
  entityType: PlayerParentEntity;
}

export const RepairPlayerModal = ({
  open,
  onClose,
  onSuccess,
  player,
  entityId,
  entityType,
}: RepairPlayerModalProps) => {
  const t = useTranslations();
  const notification = useNotifications();
  const [otpCode, setOtpCode] = useState('');
  const [hasError, setHasError] = useState(false);
  const { mutateAsync: pairDevice, isPending } = usePairDeviceMutation();

  const playerDisplayName = player.name || `Player ${player.id.slice(-4)}`;
  const canSubmit = otpCode.length === 6 && /^\d{6}$/.test(otpCode);

  const handleOtpChange = (code: string) => {
    setOtpCode(code);
    setHasError(false);
  };

  const handleSubmit = async () => {
    if (!canSubmit) return;
    try {
      await pairDevice({
        playerId: player.id,
        code: otpCode,
        entityId,
        entityType,
      });
      notification.notifySuccess(t('flow.notification.pairSuccess'));
      setOtpCode('');
      onSuccess();
      onClose();
    } catch (error) {
      console.error('Re-pair device error:', error);
      setHasError(true);
      notification.notifyError(t('flow.notification.pairError'));
    }
  };

  const handleOtpConfirm = () => {
    if (canSubmit) handleSubmit();
  };

  return (
    <Dialog open={open} onClose={onClose} onClick={(e) => e.stopPropagation()}>
      <DialogTitle>{t('player.repairTitle' as TranslationKey)}</DialogTitle>
      <DialogContent>
        <DialogContentText sx={{ mb: 2 }}>
          {t('player.repairDescription' as TranslationKey, { name: playerDisplayName })}
        </DialogContentText>
        <Stack spacing={2}>
          <Typography variant="body2" color="text.secondary">
            {t('flows.playModal.otpCodeLabel' as TranslationKey)}
          </Typography>
          <OtpCode autoFocus onChange={handleOtpChange} onConfirm={handleOtpConfirm} hasError={hasError} />
          <Stack direction="row" spacing={1} justifyContent="flex-end">
            <Button onClick={onClose}>{t('common.cancel')}</Button>
            <Button variant="contained" onClick={handleSubmit} disabled={!canSubmit || isPending}>
              {t('common.confirm')}
            </Button>
          </Stack>
        </Stack>
      </DialogContent>
    </Dialog>
  );
};
